import { ApiError } from '@/api/error';
import { computed, ref } from 'vue';
import { useToast } from './use-toast';
import { useAuthStore } from '@/stores';
import { useRouter } from 'vue-router';

export function useApi<T, K extends any[]>(cb: (...args: K) => Promise<T>) {
    const state = ref<'pending' | 'working' | 'success' | 'error'>('pending');
    const result = ref<T>();
    const errors = ref<string[]>([]);

    const router = useRouter();
    const auth = useAuthStore();
    const toast = useToast();

    const fn = async (...args: K) => {
        try {
            state.value = 'working';
            errors.value = [];
            result.value = undefined;
            const data = await cb(...args);
            result.value = data;
            state.value = 'success';
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || err.toString());
            if (err instanceof ApiError) {
                errors.value = err.errors || [];
                switch (err.statusCode) {
                    case 401:
                        auth.signout();
                        router.replace({ path: '/login', query: { continue: window.location.pathname } });
                        break;
                    case 403:
                        router.replace('/');
                        break;
                }
            }
            state.value = 'error';
        }
    };

    return {
        state,
        result,
        errors,
        fn,
        isWorking: computed(() => state.value == 'working'),
        isSuccess: computed(() => state.value == 'success'),
        isError: computed(() => state.value == 'error'),
    };
}
